import React, { useEffect, useRef } from "react";
import { Bot, Sparkles } from "lucide-react";

const SUGGESTIONS = ["What services do you offer?", "Do you do 5-year PHA revalidations?", "What is LOPA?"];
const DOTS = [0, 1, 2];

// Strip stray Markdown markers so replies render as clean plain text.
const cleanText = (t) =>
  String(t)
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/(^|\s)\*(?=\S)(.*?)\*/g, "$1$2")
    .replace(/`([^`]*)`/g, "$1");

const bubbleClass = (m) => {
  if (m.role === "user") return "bg-[#04162E] text-white";
  if (m.isNotice) return "bg-[#FFF7E6] text-[#7A5A16] border border-[#E8D3A3]";
  return "bg-white text-[#04162E] border border-[rgba(4,22,46,0.14)]";
};

const BotAvatar = () => (
  <div className="mr-2 mt-0.5 shrink-0 grid place-items-center h-7 w-7 rounded-[3px] bg-white border border-[rgba(4,22,46,0.14)]">
    <Bot className="h-4 w-4 text-[#0B6B44]" />
  </div>
);

const Message = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    {m.role === "assistant" && <BotAvatar />}
    <div
      data-testid={`chat-msg-${m.role}`}
      className={`max-w-[78%] rounded-[2px] px-3.5 py-2.5 font-body text-sm leading-relaxed whitespace-pre-wrap ${bubbleClass(m)}`}
    >
      {m.role === "assistant" ? cleanText(m.content) : m.content}
    </div>
  </div>
);

const TypingIndicator = () => (
  <div className="flex justify-start">
    <BotAvatar />
    <div className="rounded-[2px] bg-white border border-[rgba(4,22,46,0.14)] px-4 py-3">
      <div className="flex gap-1.5">
        {DOTS.map((d) => (
          <span key={d} className="h-1.5 w-1.5 rounded-full bg-[#0E8A57] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
        ))}
      </div>
    </div>
  </div>
);

const Suggestions = ({ onPick }) => (
  <div className="pt-1 space-y-2" data-testid="chat-suggestions">
    <div className="flex items-center gap-1.5 text-[11px] font-body font-semibold text-[#5C6C80]">
      <Sparkles className="h-3 w-3 text-[#0E8A57]" /> Try asking
    </div>
    {SUGGESTIONS.map((s) => (
      <button
        key={s}
        onClick={() => onPick(s)}
        className="block w-full text-left font-body text-sm rounded-[2px] border border-[rgba(4,22,46,0.14)] bg-white px-3 py-2 text-[#04162E] hover:border-[#0E8A57] transition-colors"
      >
        {s}
      </button>
    ))}
  </div>
);

export const ChatMessages = ({ messages, loading, onPick }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  return (
    <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F4F6F3]">
      {messages.map((m) => (
        <Message key={m.id} m={m} />
      ))}
      {loading && <TypingIndicator />}
      {messages.length <= 1 && !loading && <Suggestions onPick={onPick} />}
    </div>
  );
};
